/**
 * @module DemoModeContextDefinition
 * @purpose Context object + `useDemoMode` accessor hook for platform-wide demo-mode status.
 *   Split from `DemoModeContext.tsx` (house template: `theme-context.ts` / `ThemeContext.tsx`)
 *   so the provider component and the context/hook live in separate files -- this satisfies
 *   `react-refresh/only-export-components`.
 * @depends react, types/platform
 * @dependedOnBy contexts/DemoModeContext.tsx, DemoModeBanner, PlatformSettingsPage
 *
 * @rules
 * - Performance: pure context plumbing -- no data fetching here
 * - State: n/a -- state lives in DemoModeProvider
 * - Errors: useDemoMode throws when used outside a DemoModeProvider
 */

import { createContext, useContext } from 'react'
import type { PlatformStatus } from '../types/platform'

export interface DemoModeContextValue {
  status: PlatformStatus | null
  refetch: () => Promise<void>
}

export const DemoModeContext = createContext<DemoModeContextValue | undefined>(undefined)

/**
 * Access the platform demo-mode status and its refetch function.
 *
 * @behaviour `status` is null before the first successful fetch resolves, or when a fetch
 *            fails before any prior success -- callers must treat null as "unknown", not
 *            "demo mode off".
 * @errors Throws when called outside a DemoModeProvider.
 */
export function useDemoMode(): DemoModeContextValue {
  const context = useContext(DemoModeContext)
  if (!context) {
    throw new Error('useDemoMode must be used within a DemoModeProvider')
  }
  return context
}
